import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Grid } from '@mui/material';
import { Fab } from '@biq/apex-ui';
import {
    AttributeAssociationItem,
    defaultAttributeAssociationItem,
    EDGE_CONDITION_ERRORS,
    getEmptyErrorMessage,
} from '../../edge-definition-helper';
import {
    EditAttributeAssociationWidgetProps,
    editAttributeAssociationWidgetStyles,
} from './edit-edge-definition-helper';
import { EditAttributeAssociationSelection } from './EditAttributeAssociationSelection';
import { SelectedAttributeAssociation } from './SelectedAttributeAssociation';
import { SectionListEmptyError } from '../../../../components/section-list-with-search-field/SectionListEmptyError';

export const EditAttributeAssociationWidget = ({
    attributeAssociationList,
    onAttributeAssociationListChange,
    leftSideSelectionOptions,
    rightSideSelectionOptions,
}: EditAttributeAssociationWidgetProps): React.JSX.Element => {
    const [addMode, setAddMode] = useState<boolean>(false);
    const [currentSelectionItem, setCurrentSelectionItem] =
        useState<AttributeAssociationItem>(defaultAttributeAssociationItem);

    const isFirstItem: boolean = attributeAssociationList.length === 0;

    const onAddButtonClick = (): void => {
        setCurrentSelectionItem({
            ...currentSelectionItem,
            id: crypto.randomUUID(),
        });
        setAddMode(true);
    };

    const validateNonEmptyValues = (
        item: AttributeAssociationItem
    ): boolean => {
        const leftSideValueValid: boolean =
            !!item.attributeAssociation.leftSideValue;
        const rightSideValueValid: boolean =
            !!item.attributeAssociation.rightSideValue;
        const displayBothEmpty: boolean =
            !leftSideValueValid && !rightSideValueValid;

        setCurrentSelectionItem({
            ...item,
            leftSideError: !leftSideValueValid,
            leftSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !leftSideValueValid
            ),
            rightSideError: !rightSideValueValid,
            rightSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !rightSideValueValid
            ),
        });

        return leftSideValueValid && rightSideValueValid;
    };

    const validateDuplicationSelections = (
        updatedItem: AttributeAssociationItem
    ): boolean => {
        const filteredItems = attributeAssociationList.filter(
            (item) =>
                item.attributeAssociation.leftSideValue ===
                    updatedItem.attributeAssociation.leftSideValue &&
                item.attributeAssociation.rightSideValue ===
                    updatedItem.attributeAssociation.rightSideValue
        );

        if (filteredItems.length > 0) {
            setCurrentSelectionItem({
                ...updatedItem,
                leftSideError: true,
                leftSideErrorMessage: EDGE_CONDITION_ERRORS.DUPLICATE_CONDITION,
                rightSideError: true,
                rightSideErrorMessage:
                    EDGE_CONDITION_ERRORS.DUPLICATE_CONDITION,
            });
        }

        return filteredItems.length === 0;
    };

    const handleAddSelectionItem = (
        newItem: AttributeAssociationItem
    ): void => {
        const isItemWithValidSelections = validateNonEmptyValues(newItem);
        const isItemWithNonDuplicateSelections =
            validateDuplicationSelections(newItem);

        // only add to the list if current item is not empty or its associations are not duplicated.
        if (isItemWithValidSelections && isItemWithNonDuplicateSelections) {
            attributeAssociationList.push(newItem);
            onAttributeAssociationListChange(attributeAssociationList);
            setCurrentSelectionItem(defaultAttributeAssociationItem);
            setAddMode(false);
        }
    };

    const handleDelete = (indexToDelete: number): void => {
        const updatedList: AttributeAssociationItem[] =
            attributeAssociationList.filter(
                (_, index) => indexToDelete !== index
            );
        onAttributeAssociationListChange(updatedList);
    };

    const handleCancelFromAddMode = (): void => {
        setCurrentSelectionItem(defaultAttributeAssociationItem);
        setAddMode(false);
    };

    return (
        <Grid sx={editAttributeAssociationWidgetStyles.mainContainer}>
            <Grid>
                {addMode ? (
                    <EditAttributeAssociationSelection
                        leftSideSelectionOptions={leftSideSelectionOptions}
                        rightSideSelectionOptions={rightSideSelectionOptions}
                        handleRemove={handleCancelFromAddMode}
                        attributeAssociationItem={currentSelectionItem}
                        handleAddItem={(event): void =>
                            handleAddSelectionItem(event)
                        }
                        isFirstItem={attributeAssociationList.length === 0}
                    />
                ) : (
                    <Fab
                        type='button'
                        sx={editAttributeAssociationWidgetStyles.fabButton}
                        icon={<AddIcon />}
                        color='secondary'
                        label='New Attribute Association'
                        variant='extended'
                        size='small'
                        onClick={onAddButtonClick}
                    />
                )}
            </Grid>
            {attributeAssociationList.length > 0 && (
                <Grid
                    sx={
                        editAttributeAssociationWidgetStyles.selectedAssociationContainer
                    }
                >
                    {attributeAssociationList.map((association, index) => (
                        <SelectedAttributeAssociation
                            item={association}
                            onDelete={(): void => handleDelete(index)}
                            key={association.id}
                        />
                    ))}
                </Grid>
            )}
            {attributeAssociationList.length === 0 && !addMode && (
                <SectionListEmptyError
                    emptyErrorHeadingText={
                        'Need at least one attribute association.'
                    }
                    emptyErrorSubHeadingText={
                        'Attribute associations cannot be empty.'
                    }
                />
            )}
        </Grid>
    );
};
